import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, BookOpen, Trophy, Settings, X, LogOut, Terminal } from 'lucide-react';
import { GroupId } from '../../config/systemConfig';
import { GroupSelector } from './GroupSelector';
import { UserStats } from '../../types';
interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  currentView: string;
  onNavigate: (view: string) => void;
  stats: UserStats;
  selectedGroup: GroupId;
  onSelectGroup: (group: GroupId) => void;
}
export function MobileNav({
  isOpen,
  onClose,
  currentView,
  onNavigate,
  stats,
  selectedGroup,
  onSelectGroup
}: MobileNavProps) {
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const navItems = [{
    id: 'dashboard',
    label: 'Overzicht',
    icon: LayoutDashboard
  }, {
    id: 'modules',
    label: 'Vakken',
    icon: BookOpen
  }, {
    id: 'progress',
    label: 'Voortgang',
    icon: Trophy
  }, {
    id: 'settings',
    label: 'Instellingen',
    icon: Settings
  }, {
    id: 'developer',
    label: 'Developer',
    icon: Terminal,
    isDev: true
  }];
  return <AnimatePresence data-id="element-1008">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 md:hidden" data-id="element-1009" />

          {/* Drawer */}
          <motion.div initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-y-0 left-0 w-[80%] max-w-xs bg-white shadow-2xl z-50 md:hidden flex flex-col" data-id="element-1010">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-1011">
              <div className="flex items-center gap-3" data-id="element-1012">
                <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200" data-id="element-1013">
                  <span className="text-white font-bold text-xl" data-id="element-1014">C</span>
                </div>
                <span className="font-bold text-slate-900 text-lg" data-id="element-1015">
                  Cito Meester
                </span>
              </div>
              <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1016">
                <X className="w-6 h-6" data-id="element-1017" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto py-6 px-4 space-y-8" data-id="element-1018">
              {/* User Profile */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100" data-id="element-1019">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-lg border-2 border-white shadow-sm" data-id="element-1020">
                  {stats.name.charAt(0)}
                </div>
                <div data-id="element-1021">
                  <div className="font-bold text-slate-900" data-id="element-1022">{stats.name}</div>
                  <div className="text-sm text-slate-500" data-id="element-1023">
                    Level {stats.level} • {stats.xp} XP
                  </div>
                </div>
              </div>

              {/* Group Selector */}
              <div data-id="element-1024">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 block px-2" data-id="element-1025">
                  Jouw Groep
                </label>
                <GroupSelector selectedGroup={selectedGroup} onSelect={onSelectGroup} data-id="element-1026" />
              </div>

              {/* Navigation */}
              <nav className="space-y-2" data-id="element-1027">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 block px-2" data-id="element-1028">
                  Menu
                </label>
                {navItems.map(item => {
              const isActive = currentView === item.id;
              return <button key={item.id} onClick={() => {
                onNavigate(item.id);
                onClose();
              }} className={`
                        w-full flex items-center gap-4 p-3 rounded-xl transition-all duration-200
                        ${isActive ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium'}
                        ${item.isDev ? 'mt-4 border-t border-slate-100 pt-4' : ''}
                      `} data-id="element-1029">
                      <item.icon className={`w-6 h-6 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-1030" />
                      {item.label}
                    </button>;
            })}
              </nav>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-100" data-id="element-1031">
              <button className="flex items-center gap-3 text-slate-500 hover:text-rose-600 transition-colors font-medium w-full p-2 rounded-lg hover:bg-rose-50" data-id="element-1032">
                <LogOut className="w-5 h-5" data-id="element-1033" />
                Uitloggen
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}